import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ShortcutCheatSheet from './ShortcutCheatSheet';

/**
 * Returns true if the active element is a text input, textarea, select,
 * or contenteditable element — i.e. the user is typing.
 */
function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

export default function KeyboardShortcuts() {
  const navigate = useNavigate();
  const [showCheatSheet, setShowCheatSheet] = useState(false);
  const [pendingPrefix, setPendingPrefix] = useState<string | null>(null);
  const prefixTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPrefix = useCallback(() => {
    setPendingPrefix(null);
    if (prefixTimeoutRef.current) {
      clearTimeout(prefixTimeoutRef.current);
      prefixTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Never intercept when a modifier key (Ctrl/Cmd/Alt) is held,
      // except for the "?" shortcut which needs Shift.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Allow Escape to close the cheat sheet even in input fields
      if (e.key === 'Escape' && showCheatSheet) {
        setShowCheatSheet(false);
        clearPrefix();
        return;
      }

      // Don't trigger shortcuts while user is typing in form fields
      if (isInputFocused()) return;

      // "?" — toggle cheat sheet (Shift + / on most keyboards)
      if (e.key === '?') {
        e.preventDefault();
        setShowCheatSheet((prev) => !prev);
        clearPrefix();
        return;
      }

      // If we have a pending "g" prefix, handle the second key
      if (pendingPrefix === 'g') {
        e.preventDefault();
        clearPrefix();

        switch (e.key) {
          case 'a':
            navigate('/analyze');
            break;
          case 'f':
            navigate('/flip-calculator');
            break;
          case 'l':
            navigate('/listings');
            break;
          case 'p':
            navigate('/projects');
            break;
          case 'i':
            navigate('/import');
            break;
          case 'd':
            navigate('/dashboard');
            break;
          default:
            // Unknown second key — do nothing
            break;
        }
        return;
      }

      // Start a "g" prefix sequence
      if (e.key === 'g') {
        e.preventDefault();
        setPendingPrefix('g');
        // Auto-clear after 1 second if no second key is pressed
        if (prefixTimeoutRef.current) clearTimeout(prefixTimeoutRef.current);
        prefixTimeoutRef.current = setTimeout(() => {
          setPendingPrefix(null);
          prefixTimeoutRef.current = null;
        }, 1000);
        return;
      }

      // "n" — New analysis (only outside input fields, which is already checked above)
      if (e.key === 'n') {
        e.preventDefault();
        navigate('/analyze/new');
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (prefixTimeoutRef.current) clearTimeout(prefixTimeoutRef.current);
    };
  }, [navigate, pendingPrefix, showCheatSheet, clearPrefix]);

  return (
    <>
      {showCheatSheet && <ShortcutCheatSheet onClose={() => setShowCheatSheet(false)} />}
      {/* Pending prefix indicator */}
      {pendingPrefix && (
        <div className="shortcut-prefix-indicator" aria-live="polite">
          <kbd className="kbd">g</kbd>
          <span>...</span>
        </div>
      )}
    </>
  );
}
